import { useState, useRef, useMemo, useContext } from "react"
import { useHistory, useParams } from "react-router-dom"
import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { ExclamationIcon, SearchIcon } from "@heroicons/react/outline"
import { DatePicker } from "antd"
import {
  FormattedDate,
  PageTopBar,
  PageView,
  ButtonLoader
} from "../../components"
import { BasicTable } from "../../components/BasicTable/BasicTable"
import { Loader } from "../../components/Loader"
import { UserContext } from "../../hooks/UserContext"
import { fetchDeletedLostHistory } from "../../services/stockUnitServices"
import { setDeletedLostHistoryPageSize } from "../../redux/pageSizeSlice"
import { DELETE_LOST_STOCK } from "../../navigation/constants"

const { RangePicker } = DatePicker

export const DeletedLostHistoryContainer = () => {
  const { warehouse_id } = useParams()
  const history = useHistory()
  const { i18n } = useTranslation()
  const { user } = useContext(UserContext)
  const dispatch = useDispatch()
  const pageSize = useSelector((state) => state.pageSize.deletedLostHistoryList)
  const tableRef = useRef()

  const hasPermission = user?.permissions?.delete_lost_stock === true

  // Filter state (what the user sees/edits)
  const [dateRange, setDateRange] = useState(null)
  const [storeName, setStoreName] = useState('')
  const [sku, setSku] = useState('')
  const [barCode, setBarCode] = useState('')
  const [lotUniqueCode, setLotUniqueCode] = useState('')

  // Search state (what triggers the query)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchParams, setSearchParams] = useState(null)

  const columns = useMemo(() => [
    {
      Header: i18n.t('deleted_lost_history.columns.discarded_at'),
      accessor: 'discarded_at',
      disableFilters: true,
      disableSortBy: true,
      shrinkToContent: true
    },
    {
      Header: i18n.t('deleted_lost_history.columns.store_name'),
      accessor: 'store_name',
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: i18n.t('deleted_lost_history.columns.sku'),
      accessor: 'sku',
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: i18n.t('deleted_lost_history.columns.bar_code'),
      accessor: 'bar_code',
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: i18n.t('deleted_lost_history.columns.lot'),
      accessor: 'lot_unique_code',
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: i18n.t('deleted_lost_history.columns.quantity'),
      accessor: 'discarded_quantity',
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: i18n.t('deleted_lost_history.columns.location'),
      accessor: 'warehouse_location',
      disableFilters: true,
      disableSortBy: true
    },
    {
      Header: i18n.t('deleted_lost_history.columns.discarded_by'),
      accessor: 'discarded_by',
      disableFilters: true,
      disableSortBy: true
    }
  ], [i18n])

  const {
    isLoading,
    isError,
    data,
    isFetching,
    isPreviousData,
  } = useQuery(
    ['deleted-lost-history', searchParams],
    () => fetchDeletedLostHistory(searchParams),
    {
      keepPreviousData: true,
      enabled: hasPermission && hasSearched && !!searchParams
    }
  )

  const buildSearchParams = (page = 1, perPage = pageSize) => {
    const params = {
      page,
      per_page: perPage,
      warehouse_id: parseInt(warehouse_id, 10),
      start_date: dateRange ? dateRange[0].format('YYYY-MM-DD') : searchParams?.start_date,
      end_date: dateRange ? dateRange[1].format('YYYY-MM-DD') : searchParams?.end_date
    }
    if (storeName.trim()) params.store_name = storeName.trim()
    if (sku.trim()) params.sku = sku.trim()
    if (barCode.trim()) params.bar_code = barCode.trim()
    if (lotUniqueCode.trim()) params.lot_unique_code = lotUniqueCode.trim()
    return params
  }

  const handleSearch = () => {
    if (!dateRange) return
    setSearchParams(buildSearchParams(1))
    setHasSearched(true)
  }

  const handleClear = () => {
    setDateRange(null)
    setStoreName('')
    setSku('')
    setBarCode('')
    setLotUniqueCode('')
    setSearchParams(null)
    setHasSearched(false)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && dateRange) {
      handleSearch()
    }
  }

  const getTableData = () => {
    if (!data?.items) return []
    return data.items.map((item) => ({
      entity: { ...item },
      discarded_at: <FormattedDate date={item.discarded_at} shortDate />,
      store_name: item.product?.store?.name || '-',
      sku: item.product?.product_codes?.length > 0
        ? <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {item.product.product_codes.map(c => c.sku).join(', ')}
          </div>
        : '-',
      bar_code: item.product?.bar_code || '-',
      lot_unique_code: item.lot?.unique_code || '-',
      discarded_quantity: <span className="text-red-600 font-semibold">{item.discarded_quantity}</span>,
      warehouse_location: item.warehouse_location?.unique_code || '-',
      discarded_by: item.discarded_by?.email || '-'
    }))
  }

  const goBack = () => {
    history.push(DELETE_LOST_STOCK.replace(':warehouse_id', warehouse_id))
  }

  if (!hasPermission) {
    return (
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              {i18n.t('deleted_lost_history.title')}
            </div>
          </PageTopBar>
        }
      >
        <div className="flex flex-col items-center justify-center py-12">
          <ExclamationIcon className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {i18n.t('delete_lost_stock.no_permission_title')}
          </h2>
          <p className="text-gray-500 mb-6">
            {i18n.t('delete_lost_stock.no_permission_message')}
          </p>
          <ButtonLoader type="secondary" onClick={goBack}>
            {i18n.t('deleted_lost_history.go_back')}
          </ButtonLoader>
        </div>
      </PageView>
    )
  }

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"

  return (
    <PageView
      topMenu={
        <PageTopBar>
          <div className="text-lg font-semibold">
            {i18n.t('deleted_lost_history.title')}
            <span className="text-sm text-gray-400 ml-2">
              {i18n.t('common.warehouse')} {warehouse_id}
            </span>
          </div>
          <ButtonLoader type="secondary" onClick={goBack}>
            {i18n.t('deleted_lost_history.go_back')}
          </ButtonLoader>
        </PageTopBar>
      }
      childrenFullWidth={true}
      topMenuFullWidth={true}
    >
      {/* Filter Form */}
      <div className="bg-white shadow rounded-lg p-6 mx-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date Range - required */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {i18n.t('deleted_lost_history.filters.date_range')} <span className="text-red-600">*</span>
            </label>
            <RangePicker
              value={dateRange}
              onChange={(values) => setDateRange(values)}
              format="YYYY-MM-DD"
              placeholder={[
                i18n.t('deleted_lost_history.filters.start_date'),
                i18n.t('deleted_lost_history.filters.end_date')
              ]}
              className="w-full"
              size="middle"
              status={!dateRange ? 'error' : undefined}
            />
            {!dateRange && (
              <p className="mt-1 text-xs text-red-600">{i18n.t('inputPlaceholder.required_field')}</p>
            )}
          </div>

          {/* Store Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {i18n.t('deleted_lost_history.filters.store_name')}
            </label>
            <input
              type="text"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              onKeyDown={handleKeyDown}
              className={inputClass}
              placeholder={i18n.t('deleted_lost_history.filters.store_name')}
            />
          </div>

          {/* SKU */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {i18n.t('deleted_lost_history.filters.sku')}
            </label>
            <input
              type="text"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              onKeyDown={handleKeyDown}
              className={inputClass}
              placeholder={i18n.t('deleted_lost_history.filters.sku')}
            />
          </div>

          {/* UPC / Bar Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {i18n.t('deleted_lost_history.filters.bar_code')}
            </label>
            <input
              type="text"
              value={barCode}
              onChange={(e) => setBarCode(e.target.value)}
              onKeyDown={handleKeyDown}
              className={inputClass}
              placeholder={i18n.t('deleted_lost_history.filters.bar_code')}
            />
          </div>

          {/* Lot */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {i18n.t('deleted_lost_history.filters.lot')}
            </label>
            <input
              type="text"
              value={lotUniqueCode}
              onChange={(e) => setLotUniqueCode(e.target.value)}
              onKeyDown={handleKeyDown}
              className={inputClass}
              placeholder={i18n.t('deleted_lost_history.filters.lot')}
            />
          </div>

          {/* Buttons */}
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={handleSearch}
              disabled={!dateRange}
              className={`inline-flex items-center justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-sm font-medium text-white ${
                !dateRange
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
              }`}
            >
              <SearchIcon className="h-4 w-4 mr-1" />
              {i18n.t('deleted_lost_history.filters.search')}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {i18n.t('deleted_lost_history.filters.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {!hasSearched ? (
        <div className="text-center py-12 text-gray-500">
          <SearchIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{i18n.t('deleted_lost_history.initial_message')}</p>
        </div>
      ) : !isPreviousData && isLoading ? (
        <Loader show={true} />
      ) : isError ? (
        <div className="text-center py-8 text-red-500">
          {i18n.t('deleted_lost_history.error_loading')}
        </div>
      ) : (
        <>
          {data?.total_deleted !== undefined && (
            <div className="mb-4 px-4 text-sm text-gray-600 font-medium">
              {i18n.t('deleted_lost_history.total_deleted', { count: data.total_deleted })}
            </div>
          )}
          <BasicTable
            ref={tableRef}
            showHeader
            showLoader={isFetching && isPreviousData}
            columns={columns}
            showPaginator
            data={getTableData()}
            paginationMeta={data?.meta}
            labelToPaginator={i18n.t('deleted_lost_history.records')}
            pagesSize={searchParams?.per_page}
            onPaginationChange={(requestedPage) => {
              setSearchParams(buildSearchParams(requestedPage))
            }}
            onPageSizeChange={(newPageSize) => {
              dispatch(setDeletedLostHistoryPageSize(newPageSize))
              setSearchParams(buildSearchParams(1, newPageSize))
              tableRef.current.resetPagination()
            }}
            showDataStatus
            isFetching={isFetching}
            emptyTableText={i18n.t('deleted_lost_history.empty_table')}
          />
        </>
      )}
    </PageView>
  )
}
